import { CardBody } from 'components';
import { Accordion } from 'components/atoms/Accordion/Accordion';
import { CustomPropertyInput } from 'components/atoms/CustomPropertyInput/CustomPropertyInput';
import { CustomPropertyLabel } from 'components/atoms/CustomPropertyLabel/CustomPropertyLabel';
import { CustomPropertySelect } from 'components/atoms/CustomPropertySelect/CustomPropertySelect';
import { usePropertyDetails } from '../hooks/PropertyDetails.hook';
import { Price, VehicleData } from '../types/Add-subscription.types';
import './PropertyDetails.css';

interface PropertyDetailsProps {
  counts: Record<number, number>;
  prices: Price[];
  period: string;
  onValidChange: (isValid: boolean) => void;
  formData: Record<string, Record<string, string>>;
  setFormData: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, string>>>
  >;
  vehicleData: VehicleData;
}

const countryOptions = [
  'Puerto Rico',
  'Costa Rica',
  'Panama',
  'Colombia',
  'México',
];

export function PropertyDetails({
  counts,
  prices,
  period,
  onValidChange,
  formData,
  setFormData,
  vehicleData,
}: PropertyDetailsProps) {
  const {
    propertyEntries2,
    handleInputChange,
    selectedBrands,
    setSelectedBrands,
    getIcon,
  } = usePropertyDetails({
    prices,
    period,
    counts,
    formData,
    setFormData,
    onValidChange,
  });

  if (propertyEntries2.length === 0) return null;

  return (
    <CardBody>
      <header className="addon-group__header">
        <h2 className="addon-group__title">PROPIEDADES</h2>
        <p className="addon-group__description">
          Completa los siguientes datos
        </p>
      </header>
      <div className="space-y-6">
        <Accordion title="Información de propiedades" isOpenAccordion={true}>
          {propertyEntries2.map((entry) => (
            <div key={`${entry.id}-${entry.count}`} className="custom-box">
              <div className="custom-container">
                <img src={getIcon(entry.type)} alt="Tipo de propiedad" />
                <h4 className="custom-title">
                  {entry.alias_name} {entry.count}
                </h4>
              </div>
              <div className="custom-grid">
                {entry.properties
                  .sort((a, b) => a.order_number - b.order_number)
                  .map((prop) => (
                    <div key={prop.id} className="custom-field">
                      <CustomPropertyLabel
                        htmlFor={`${entry.id}-${entry.count}-${prop.id}`}
                        required={prop.required}
                      >
                        {prop.name}
                      </CustomPropertyLabel>
                      {prop.type === 'select' ? (
                        prop.name === 'Marca' ? (
                          <CustomPropertySelect
                            id={`${entry.id}-${entry.count}-${prop.id}`}
                            value={
                              formData[`${entry.id}-${entry.count}`]?.[
                                prop.name
                              ] || ''
                            }
                            onChange={(value) => {
                              handleInputChange(
                                `${entry.id}-${entry.count}`,
                                prop.name,
                                value
                              );
                              setSelectedBrands((prev) => ({
                                ...prev,
                                [`${entry.id}-${entry.count}`]: value,
                              }));
                            }}
                            options={Object.keys(
                              vehicleData[
                                entry.type.toLocaleLowerCase() === 'vehiculos'
                                  ? 'autos'
                                  : 'motos'
                              ]
                            )}
                            placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                          />
                        ) : prop.name === 'Modelo' ? (
                          <CustomPropertySelect
                            id={`${entry.id}-${entry.count}-${prop.id}`}
                            value={
                              formData[`${entry.id}-${entry.count}`]?.[
                                prop.name
                              ] || ''
                            }
                            onChange={(value) =>
                              handleInputChange(
                                `${entry.id}-${entry.count}`,
                                prop.name,
                                value
                              )
                            }
                            options={
                              selectedBrands[`${entry.id}-${entry.count}`]
                                ? vehicleData[
                                    entry.type.toLocaleLowerCase() ===
                                    'vehiculos'
                                      ? 'autos'
                                      : 'motos'
                                  ][
                                    selectedBrands[`${entry.id}-${entry.count}`]
                                  ] || []
                                : []
                            }
                            placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                          />
                        ) : prop.name === 'Año' && entry.max_property_age ? (
                          <CustomPropertySelect
                            id={`${entry.id}-${entry.count}-${prop.id}`}
                            value={
                              formData[`${entry.id}-${entry.count}`]?.[
                                prop.name
                              ] || ''
                            }
                            onChange={(value) =>
                              handleInputChange(
                                `${entry.id}-${entry.count}`,
                                prop.name,
                                value
                              )
                            }
                            options={(() => {
                              const currentYear = new Date().getFullYear();
                              const minYear =
                                entry.min_property_age === null
                                  ? currentYear
                                  : currentYear - entry.min_property_age;
                              const maxYear =
                                currentYear - entry.max_property_age;
                              return Array.from(
                                { length: minYear - maxYear + 1 },
                                (_, i) => (minYear - i).toString()
                              );
                            })()}
                            placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                          />
                        ) : prop.name === 'Pais' ? (
                          <CustomPropertySelect
                            id={`${entry.id}-${entry.count}-${prop.id}`}
                            value={
                              formData[`${entry.id}-${entry.count}`]?.[
                                prop.name
                              ] || ''
                            }
                            onChange={(value) =>
                              handleInputChange(
                                `${entry.id}-${entry.count}`,
                                prop.name,
                                value
                              )
                            }
                            options={countryOptions}
                            placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                          />
                        ) : null
                      ) : (
                        <CustomPropertyInput
                          id={`${entry.id}-${entry.count}-${prop.id}`}
                          type={prop.type === 'number' ? 'number' : 'text'}
                          placeholder={prop.name}
                          value={
                            formData[`${entry.id}-${entry.count}`]?.[
                              prop.name
                            ] || ''
                          }
                          onChange={(value) =>
                            handleInputChange(
                              `${entry.id}-${entry.count}`,
                              prop.name,
                              value
                            )
                          }
                          required={prop.required}
                        />
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </Accordion>
      </div>
    </CardBody>
  );
}
